import { select, extent, scaleLinear, zoom, quadtree, axisBottom, axisLeft, mouse, event, color, max } from "d3";

class Global {

	constructor (artists, songs, callbacks) {

		this.artists = artists;
		this.songs = songs;
    
		this.callbacks = callbacks;

		this.data = [];
		this.filteredData = [];
    
		this.calculateData();

		// Constants
		this.SEARCH_RADIUS = max(this.data, d => d.size);
		this.ZOOM_DELAY = 100;
    
		// Filters
		this.filters = {
			dateRange: null,
			country: null
		};
    
		// Selected Point
		this.selectedPoint = null;
		this.searchedPoint = null;

		// Zoom Timeout function
		this.zoomEndTimeout;
    
		// Layout
		this.fullWidth = 1300;
		this.fullHeight = 700;
		this.margin = {
			top: 60,
			right: 0,
			bottom: 30,
			left: 30
		};
		this.width = this.fullWidth - this.margin.left - this.margin.right;
		this.height = this.fullHeight - this.margin.top - this.margin.bottom;
    
		// Scales

		this.scales = {
			color: null,
			x: null,
			y: null,
			x_: null, // Constant ref scale
			y_: null // Constant ref scale
		};

		this.xRange = extent(this.data, function (d) { return d.x; });
		this.yRange = extent(this.data, function (d) { return d.y; });
    
		this.scales.x_ = scaleLinear()
			.domain([this.xRange[0] - 0.2, 4])
			.range([0, this.width]);

		this.scales.y_ = scaleLinear()
			.domain([this.yRange[0] - 0.05, this.yRange[1] + 0.15])
			.range([this.height, 0]);

		this.scales.x = this.scales.x_;
		this.scales.y = this.scales.y_;

		this.scales.color = scaleLinear()
			.domain([0, 1])
			.range(["rgba(253, 194, 106, 0.5)", "rgba(58, 161, 219, 0.5)"]);
      
		// Axis
		this.axis = {
			x: null,
			y: null,
			xSVG: null,
			ySVG: null
		};
    
		// Lines
		this.lines = {
			x: null,
			y: null,
			diagonal: null
		};

		// Draw
    
		this.canvas = null;
		this.context = null;
    
		// Random indexes
		this.randomIndex = [];
    
		// QuadTree
		this.quadTree = null;

		// Draw SVG Layout
		this.drawLayout();
    
		// Render points
		this.render();
	}
  
	render () {
		this.selectedPoint = null;
		this.filterData();
		this.generateQuadTree();
		this.findRandomIndexes();
		this.drawPoints();
	}
  
	drawLayout () {

		// Base

		var svgWrapper = select("#axis-svg")
			.attr("width", this.fullWidth)
			.attr("height", this.fullHeight);

		var svg = svgWrapper.append("g")
			.attr("transform", "translate(" + this.margin.left + "," + this.margin.top + ")");
      
		// Color gradient for the color scale
		var defs = svg.append("defs");
		var linearGradient = defs.append("linearGradient")
			.attr("id", "linear-gradient");
		linearGradient
			.attr("x1", "0%")
			.attr("y1", "0%")
			.attr("x2", "100%")
			.attr("y2", "0%");

		linearGradient.append("stop")
    	.attr("offset", "0%")
			.attr("stop-color", this.scales.color(1));
		
		linearGradient.append("stop")
    	.attr("offset", "100%")
			.attr("stop-color", this.scales.color(0));
    
		// Draw axis
		this.axis.x = axisBottom()
			.scale(this.scales.x)
			.tickSizeInner(-this.height)
			.tickSizeOuter(0);
  
		this.axis.y = axisLeft()
			.scale(this.scales.y)
			.tickSizeInner(-this.width)
			.tickSizeOuter(0);
      
		this.axis.xSVG = svg.append("g")
			.attr("class", "x axis")
			.attr("transform", "translate(0," + this.height + ")")
			.call(this.axis.x);

		this.axis.ySVG = svg.append("g")
			.attr("class", "y axis")
			.call(this.axis.y);
      
		this.lines.x = svg.append("line")
			.attr("class", "line")
			.attr("x1", this.scales.x(0))
			.attr("y1", this.scales.y(0))
			.attr("x2", this.width)
			.attr("y2", this.scales.y(0))
			.attr("stroke-width", 2)
			.attr("stroke", "#d1d1d1");
      
		this.lines.y = svg.append("line")
			.attr("class", "line")
			.attr("x1", this.scales.x(0))
			.attr("y1", this.scales.y(0))
			.attr("x2", this.scales.x(0))
			.attr("y2", 0)
			.attr("stroke-width", 2)
			.attr("stroke", "#d1d1d1");
      
		this.lines.diagonal = svg.append("line")
			.attr("class", "line diagonal")
			.attr("x1", this.scales.x(0))
			.attr("y1", this.scales.y(0))
			.attr("x2", this.scales.x(100))
			.attr("y2", this.scales.y(100))
			.attr("stroke-width", 1)
			.attr("stroke", "#000000")
			.attr("stroke-dasharray", "10,10");
      
		// Hide 

		var hide = svgWrapper.append("g").style("fill", "white");

		hide.append("rect")
			.attr("x", 0)
			.attr("y", 0)
			.attr("width", this.fullWidth)
			.attr("height", this.margin.top);
    
		hide.append("rect")
			.attr("x", 0)
			.attr("y", 0)
			.attr("width", this.margin.left)
			.attr("height", this.fullHeight);
    
		hide.append("rect")
			.attr("x", 0)
			.attr("y", this.fullHeight - this.margin.bottom)
			.attr("width", this.fullWidth)
			.attr("height", this.fullHeight - this.margin.bottom);
      
		// Color Legend
    
		this.legend = {
			height: 10,
			width: 400
		};

		let legendGroup = svg.append("g").attr("transform", "translate(" + (this.width - this.legend.width - 10) + ",10)");
  
		legendGroup.append("rect")
			.attr("width", this.legend.width)
			.attr("height", 30)
			.style("fill", "white");

		legendGroup.append("rect")
			.attr("width", this.legend.width)
			.attr("height", this.legend.height)
			.style("fill", "url(#linear-gradient)");

		legendGroup.append("text")
			.attr("transform", "translate(0," + (this.legend.height + 15) + ")")
			.style("font-size", 13)
			.text("Mostly adapted from others");

		legendGroup.append("text")
			.attr("transform", "translate(" + this.legend.width + "," + (this.legend.height + 15) + ")")
			.style("text-anchor", "end")
			.style("font-size", 13)
			.text("Mostly was adapted by others");
      
		// Axis Text

		svgWrapper.append("text")
			.attr("transform", "translate(" + (this.fullWidth / 2 + this.margin.left) + " ," + (this.fullHeight - this.margin.bottom + 20) + ")")
			.style("text-anchor", "middle")
			.text("Inspiring →");

		svgWrapper.append("text")
			.attr("transform", "rotate(-90) scale(1)")
			.attr("y", 0)
			.attr("x", 0 - (this.fullHeight / 2))
			.attr("dy", "1em")
			.style("text-anchor", "middle")
			.text("Inspired →");
    
		// Create Canvas
    
		this.canvas = select("#plot-canvas")
			.attr("width", this.width - 1)
			.attr("height", this.height - 1)
			.style("transform", "translate(" + (this.margin.left + 1) +
      "px" + "," + (this.margin.top + 1) + "px" + ")");

		this.canvas.on("click", this.onMouseClick(this));
		this.canvas.on("mousemove", this.onMousemove(this));
		this.canvas.on("mouseout", this.onMouseOut(this));
  
		// create zoom behaviour
		var zoomBehaviour = zoom()
			.scaleExtent([1, 10])
			.extent([[0, 0], [this.width, this.height]])
			.on("zoom", this.onZoom(this))
			.on("end", this.onZoomEnd(this));
      
		this.canvas.call(zoomBehaviour);

		this.context = this.canvas.node().getContext("2d");
	}
  
	drawPoints (index) {
		this.context.clearRect(0, 0, this.fullWidth, this.fullHeight);

		// if an index parameter is supplied, we only want to draw points
		// with indices in that array
		if (index) {
			index.forEach((i) => {
				this.drawPoint(this.data[i]);
			});
		}
		// draw the full dataset otherwise
		else {
			for (let i = 0; i < this.filteredData.length; i++) {
				if (i !== this.selectedPoint && i != this.searchedPoint) {
					this.drawPoint(this.filteredData[i]);
				}
			}
		}

		// Draw at the end
		if (this.selectedPoint != null) {
			this.drawPoint(this.data[this.selectedPoint], true);
		}
		if (this.searchedPoint != null) {
			this.drawPoint(this.data[this.searchedPoint], true);
		}
	}
  
	drawPoint(point, selected = false) {
		var r = point.size;
		var cx = this.scales.x(point.x);
		var cy = this.scales.y(point.y);

		let pointColor = color(this.scales.color(point.ratio));

		if (selected) {
			pointColor.opacity = 0.5;
			this.context.strokeStyle = "#000000";
		} else {
			pointColor.opacity = 0.3;
			this.context.strokeStyle = pointColor.darker();
		}

		this.context.lineWidth = 1;
		this.context.fillStyle = pointColor;

		// NOTE; each point needs to be drawn as its own path
		// as every point needs its own stroke. you can get an insane
		// speed up if the path is closed after all the points have been drawn
		// and don't mind points not having a stroke
		this.context.beginPath();
		this.context.arc(cx, cy, r, 0, 2 * Math.PI);
		this.context.stroke();
		this.context.closePath();
		this.context.fill();
	}

	calculateData () {
		Object.keys(this.artists).forEach(key => {
			this.artists[key].score_level_0 = 0;
			this.artists[key].score_level_plus_1 = 0;
			this.artists[key].score_level_minus_1 = 0;
		});
  
		// Level 0 and +1
		Object.keys(this.songs).forEach(key => {
			let song = this.songs[key];
			song.artist_ids.forEach(artistId => {
				this.artists[artistId].score_level_0++;
				if (song.parent_id) {
					this.artists[artistId].score_level_plus_1++;
				}
			});
		});
  
		// Level -1
		Object.keys(this.songs).forEach(key => {
			let song = this.songs[key];
			let parentSong = this.songs[song.parent_id];
			if (parentSong) {
				parentSong.artist_ids.forEach(id => {
					this.artists[id].score_level_minus_1++;
				});
			}
		});
  
		this.data = Object.values(this.artists)
			.filter(artist => artist.score_level_0 > 4)
			.map((artist, i) => {
				let total = artist.score_level_0;
				let x = artist.score_level_minus_1 / total;
				let y = artist.score_level_plus_1 / total;
				return {
					x: x,
					y: y,
					ratio: Math.atan(y / x) / (Math.PI / 2) || 0,
					id: artist.id,
					i: i,
					size: artist.score_level_0 / 5,
					label: artist.name,
					year: parseInt(artist.birth_year.split("-")[0]),
					country: artist.country_activity,
				};
			});
      
		this.filteredData = this.data;
	}
  
	filterData () {
		this.filteredData = this.data.filter(d => {
			return (
				this.filters.dateRange == null ||
        (
        	this.filters.dateRange != null &&
          d.year &&
          d.year >= this.filters.dateRange[0] && 
          d.year <= this.filters.dateRange[1]
        )
			) && (
				this.filters.country == null ||
        (
        	this.filters.country != null &&
        	this.filters.country == d.country
        )
			);
		});
	}
  
	findRandomIndexes() {
		this.randomIndex = this.filteredData.filter(d => d.size > 2).map(d => d.i);
	}
  
	generateQuadTree() {
		this.quadTree = quadtree()
			.x(d => this.scales.x(d.x))
			.y(d => this.scales.y(d.y))
			.addAll(this.filteredData);
	}
  
	findIntersection(xClicked, yClicked) {
		var hits = [];

		var r = this.SEARCH_RADIUS,
			nx1 = xClicked - r,
			nx2 = xClicked + r,
			ny1 = yClicked - r,
			ny2 = yClicked + r;
    
		this.quadTree.visit((node, x0, y0, x1, y1) => {
			if (!node.length) {
				var x = xClicked - this.scales.x(node.data.x),
					y = yClicked - this.scales.y(node.data.y),
					l = Math.sqrt(x * x + y * y);
          
				if (l < node.data.size) {
					hits.push(node.data);
				}
			}
			return x0 > nx2 || x1 < nx1 || y0 > ny2 || y1 < ny1;
		});
    
		if (hits.length > 0) {
			let searchedPoint = hits.find(h => h.i == this.searchedPoint);
			if (searchedPoint) {
				return searchedPoint.i;
			}
			return hits.sort((a,b) => a.size < b.size ? -1 : 1)[0].i;
		}

		return null;
	}
  
	onMouseClick(ctx) {
		return function() {

			const m = mouse(this);
      
			var xClicked = m[0];
			var yClicked = m[1];
      
			let newSelection = ctx.findIntersection(xClicked, yClicked);
      
			if (newSelection != null) {
				ctx.callbacks.onClick(ctx.data[newSelection].id);
			}
		};
	}

	onMousemove(ctx) {

		return function() {
			const m = mouse(this);

			// map the clicked point to the data space
			var xClicked = m[0];
			var yClicked = m[1];
    
			let newSelection = ctx.findIntersection(xClicked, yClicked);
    
			if (newSelection != null) {
				if (ctx.selectedPoint != newSelection) {
					ctx.selectedPoint = newSelection;
					ctx.drawPoints();
					ctx.callbacks.onHover(ctx.data[ctx.selectedPoint].id);
				}
			} else {
				if (ctx.selectedPoint != null) {
					ctx.selectedPoint = null;
					ctx.drawPoints();
					ctx.callbacks.onHover(null);
				}
			}
		};
	}
  
	onMouseOut(ctx) {
		return function() {
			ctx.callbacks.onHover(null);
		};
	}
  
	onZoom(ctx) {

		return function() {

			ctx.scales.x = event.transform.rescaleX(ctx.scales.x_);
			ctx.scales.y = event.transform.rescaleY(ctx.scales.y_);

			clearTimeout(ctx.zoomEndTimeout);
			ctx.drawPoints(ctx.randomIndex);
    
			ctx.axis.xSVG.call(ctx.axis.x.scale(ctx.scales.x));
			ctx.axis.ySVG.call(ctx.axis.y.scale(ctx.scales.y));
    
			let x0 = ctx.scales.x(0);
			let y0 = ctx.scales.y(0);
    
			ctx.lines.x.attr("x1", x0).attr("x2", ctx.width).attr("y1", y0).attr("y2", y0);
			ctx.lines.y.attr("x1", x0).attr("x2", x0).attr("y1", y0).attr("y2", 0);
			ctx.lines.diagonal.attr("x1", ctx.scales.x(0))
				.attr("y1", ctx.scales.y(0))
				.attr("x2", ctx.scales.x(100))
				.attr("y2", ctx.scales.y(100));
		};
      
	}
  
	onZoomEnd(ctx) {
		return function() {
      
			ctx.scales.x = event.transform.rescaleX(ctx.scales.x_);
			ctx.scales.y = event.transform.rescaleY(ctx.scales.y_);
      
			// when zooming is stopped, create a delay before
			// redrawing the full plot
			ctx.zoomEndTimeout = setTimeout(() => {
				ctx.generateQuadTree();
				ctx.drawPoints();
			}, ctx.ZOOM_DELAY);
		};
	}
  
	selectById(id) {
		if (id == null) {
			this.searchedPoint = null;
		} else { 
			let point = this.filteredData.find(d => {
				return d.id == id;
			});
			if (point) {
				this.selectedPoint = null;
				this.searchedPoint = point.i;
			}
		}
    
		this.drawPoints();
	}
  
	filterByDate(range) {
		this.filters.dateRange = range;
		this.render();
	}
  
	filterByCountry(country) {
		this.filters.country = country;
		this.render();
	}
}

export default Global;
